// Anchor stops mapping celsius to perceptually-uniform oklch colors.
// Hues run from cold blue (240°) → cyan → green-yellow → warm yellow → orange-red.
const TEMP_STOPS: ReadonlyArray<{ t: number; L: number; C: number; H: number }> = [
  { t: 0, L: 0.78, C: 0.10, H: 240 },
  { t: 10, L: 0.82, C: 0.09, H: 200 },
  { t: 20, L: 0.86, C: 0.13, H: 130 },
  { t: 28, L: 0.78, C: 0.16, H: 75 },
  { t: 35, L: 0.70, C: 0.19, H: 35 },
];

export function tempColor(celsius: number): string {
  const first = TEMP_STOPS[0]!;
  const last = TEMP_STOPS[TEMP_STOPS.length - 1]!;
  if (celsius <= first.t) return `oklch(${first.L} ${first.C} ${first.H})`;
  if (celsius >= last.t) return `oklch(${last.L} ${last.C} ${last.H})`;
  for (let i = 0; i < TEMP_STOPS.length - 1; i++) {
    const a = TEMP_STOPS[i]!;
    const b = TEMP_STOPS[i + 1]!;
    if (celsius >= a.t && celsius <= b.t) {
      const k = (celsius - a.t) / (b.t - a.t);
      const L = a.L + (b.L - a.L) * k;
      const C = a.C + (b.C - a.C) * k;
      const H = a.H + (b.H - a.H) * k;
      return `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)})`;
    }
  }
  return `oklch(${first.L} ${first.C} ${first.H})`;
}

export function computeTempScale(items: ReadonlyArray<{ tempMinC: number; tempMaxC: number }>) {
  const rawMin = Math.min(...items.map((i) => i.tempMinC));
  const rawMax = Math.max(...items.map((i) => i.tempMaxC));
  return {
    globalMin: Math.floor(rawMin / 2) * 2 - 2,
    globalMax: Math.ceil(rawMax / 2) * 2 + 2,
  };
}

interface TempBarProps {
  dayMin: number;
  dayMax: number;
  globalMin: number;
  globalMax: number;
  /** Optional current temperature; renders a marker dot at its position on the scale. */
  current?: number;
  className?: string;
}

export function TempBar({
  dayMin,
  dayMax,
  globalMin,
  globalMax,
  current,
  className = "flex-1 min-w-12",
}: TempBarProps) {
  const range = globalMax - globalMin || 1;
  const leftPct = ((dayMin - globalMin) / range) * 100;
  const rightPct = ((dayMax - globalMin) / range) * 100;
  const widthPct = Math.max(rightPct - leftPct, 6);
  const currentPct =
    current === undefined ? null : Math.min(100, Math.max(0, ((current - globalMin) / range) * 100));
  return (
    <div className={`relative h-1 rounded-full bg-ink/10 ${className}`}>
      <div
        className="absolute inset-y-0 rounded-full"
        style={{
          left: `${leftPct}%`,
          width: `${widthPct}%`,
          background: `linear-gradient(to right, ${tempColor(dayMin)}, ${tempColor(dayMax)})`,
        }}
      />
      {currentPct !== null && (
        <span
          className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            left: `${currentPct}%`,
            background: tempColor(current!),
            boxShadow: "0 0 0 2px oklch(1 0 0), 0 1px 2px oklch(0 0 0 / 0.25)",
          }}
        />
      )}
    </div>
  );
}
